import React, { useState } from 'react';
import { safeDashboardData, safeLength, safeFilter } from '../utils/arrayUtils';
import { useAuth } from '../contexts/AuthContext';
import { 
  Users, Server, ShoppingCart, Wrench, DollarSign, Building2,
  Smartphone, Wifi, Scale, Activity, TrendingUp, AlertTriangle,
  CheckCircle, Clock, Plus, Eye, ArrowRight, MessageCircle,
  ChevronDown, ChevronRight
} from 'lucide-react';
import { 
  MobileStatCard, MobileQuickAction, MobileStatusCard, 
  MobileGrid, MobileSection 
} from './MobileOptimizedCards';
import { apiService } from '../services/api';
import { useOnlineUsers } from '../hooks/useOnlineUsers';
import { useMessaging } from '../hooks/useMessaging';
import MobileUpdateNotification from './MobileUpdateNotification';
import MessageModal from './MessageModal';
import GroupChatModal from './GroupChatModal';

interface MobileDashboardProps {
  onNavigate: (page: string) => void;
  onStartVideoCall?: (userId: number | null, callType: 'audio' | 'video', callId: number, roomName: string) => void;
}

const MobileDashboard: React.FC<MobileDashboardProps> = ({ onNavigate, onStartVideoCall }) => {
  const { user } = useAuth();
  const { onlineUsers } = useOnlineUsers();
  const { unreadCounts } = useMessaging();
  
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showGroupChat, setShowGroupChat] = useState(false);
  const [usersExpanded, setUsersExpanded] = useState(false);
  interface User {
    id: number;
    name: string;
    is_active: boolean;
    role: string;
    avatar?: string;
  }

  interface Order {
    id: number;
    status: string;
  }

  interface TechnicalService {
    id: number;
    status: string;
    updated_at: string;
  }

  const [dashboardData, setDashboardData] = useState<{
    users: User[];
    orders: Order[];
    technicalServices: TechnicalService[];
  }>({
    users: [],
    orders: [],
    technicalServices: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    loadMobileDashboardData();
  }, []);

  const loadMobileDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [usersResponse, ordersResponse, technicalServicesResponse] = await Promise.all([
        apiService.getUsers().catch(() => []),
        apiService.getOrders().catch(() => []),
        apiService.getTechnicalServices().catch(() => [])
      ]);

      setDashboardData({
        users: usersResponse,
        orders: ordersResponse,
        technicalServices: technicalServicesResponse
      });
    } catch (error) {
      console.error('Error loading mobile dashboard data:', error);
      setError('Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const { users, orders, technicalServices } = safeDashboardData(dashboardData);

  const stats = {
    users: {
      total: safeLength(users),
      active: safeFilter(users, u => u.is_active !== false).length,
      online: safeFilter(onlineUsers, u => u.is_online).length
    },
    services: {
      activeOrders: safeFilter(orders, o => ['beklemede', 'onaylandi', 'siparis-verildi'].includes(o.status)).length,
      pendingServices: safeFilter(technicalServices, s => ['yeni', 'devam-ediyor'].includes(s.status)).length,
      completedToday: safeFilter(technicalServices, s =>
        s.status === 'tamamlandi' &&
        new Date(s.updated_at).toDateString() === new Date().toDateString()
      ).length
    }
  };

  const quickActions = [
    {
      title: 'Ekip Sohbeti',
      description: 'Grup mesajlaşması',
      icon: MessageCircle,
      color: 'bg-primary',
      action: () => setShowGroupChat(true)
    },
    {
      title: 'Yeni Sipariş',
      description: 'Hızlı sipariş oluştur',
      icon: Plus,
      color: 'bg-green-600',
      action: () => onNavigate('orders')
    },
    {
      title: 'Teknik Servis',
      description: 'Arıza kaydı oluştur',
      icon: Wrench,
      color: 'bg-purple-600',
      action: () => onNavigate('technical-service')
    },
    {
      title: 'CAPEX Takibi',
      description: 'Bütçe durumunu görüntüle',
      icon: DollarSign,
      color: 'bg-green-600',
      action: () => onNavigate('capex')
    },
    {
      title: 'Sunucu Durumu',
      description: 'Altyapı sağlığını kontrol et',
      icon: Server,
      color: 'bg-orange-600',
      action: () => onNavigate('physical-servers')
    }
  ];

  if (loading) {
    return (
      <div className="pb-20 p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-gray-600">Yükleniyor...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pb-20 p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-red-600 mb-4">{error}</div>
            <button 
              onClick={() => {
                setError(null);
                loadMobileDashboardData();
              }}
              className="px-4 py-2 bg-primary text-white rounded-lg"
            >
              Tekrar Dene
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-20 min-h-screen overflow-x-hidden">
      {/* Update Notification for Mobile Dashboard */}
      <MobileUpdateNotification variant="banner" position="top" className="mb-4" />

      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-primary to-primary-dark text-white p-4 sm:p-6 rounded-b-2xl mb-6">
        <h1 className="text-xl sm:text-2xl font-bold mb-2">IT Toolbox</h1>
        <p className="text-accent text-sm sm:text-base">Sistem durumu ve hızlı erişim</p>
        
        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-2 sm:gap-4 mt-6">
          <div className="text-center">
            <div className="text-xl sm:text-2xl font-bold">{stats.users.online}</div>
            <div className="text-xs sm:text-sm text-accent">Çevrimiçi</div>
          </div>
          <div className="text-center">
            <div className="text-xl sm:text-2xl font-bold">{stats.services.activeOrders}</div>
            <div className="text-xs sm:text-sm text-accent">Aktif Sipariş</div>
          </div>
          <div className="text-center">
            <div className="text-xl sm:text-2xl font-bold">{stats.services.pendingServices}</div>
            <div className="text-xs sm:text-sm text-accent">Bekleyen Servis</div>
          </div>
        </div>
      </div>

      <div className="px-3 sm:px-4 space-y-4 sm:space-y-6">
        {/* Main Stats */}
        <MobileSection title="Genel Durum">
          <MobileGrid cols={2}>
            <MobileStatCard
              title="Toplam Kullanıcı"
              value={stats.users.total}
              change="+5.2%"
              changeType="increase"
              icon={Users}
              color="bg-primary"
              onClick={() => onNavigate('users')}
            />
            <MobileStatCard
              title="Aktif Siparişler"
              value={stats.services.activeOrders}
              change="Güncel"
              changeType="neutral"
              icon={ShoppingCart}
              color="bg-green-600"
              onClick={() => onNavigate('orders')}
            />
          </MobileGrid>
        </MobileSection>

        {/* Quick Actions */}
        <MobileSection title="Hızlı İşlemler">
          <div className="space-y-3">
            {quickActions.map((action, index) => (
              <MobileQuickAction
                key={index}
                title={action.title}
                description={action.description}
                icon={action.icon}
                color={action.color}
                onClick={action.action}
              />
            ))}
          </div>
        </MobileSection>

        {/* Online Users - Collapsible */}
        <div className="mb-4">
          <button
            onClick={() => setUsersExpanded(prev => !prev)}
            className="w-full flex items-center justify-between py-2 px-1"
          >
            <div className="flex items-center gap-2">
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                Kullanıcılar
              </h2>
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                {safeFilter(onlineUsers, u => u.is_online).length} çevrimiçi
              </span>
              <span className="text-xs text-gray-400 dark:text-gray-500">
                / {safeLength(onlineUsers)}
              </span>
            </div>
            {usersExpanded
              ? <ChevronDown className="w-4 h-4 text-gray-400 dark:text-gray-500" />
              : <ChevronRight className="w-4 h-4 text-gray-400 dark:text-gray-500" />
            }
          </button>

          {usersExpanded && (
            <div className="mt-2">
              {safeLength(onlineUsers) === 0 ? (
                <div className="text-center py-6 text-gray-500 dark:text-gray-400 text-sm">
                  Çevrimiçi kullanıcı yok
                </div>
              ) : (
                <div className="space-y-2">
                  {onlineUsers.map((user) => {
                    const unreadCount = unreadCounts[user.id] || 0;
                    return (
                      <div
                        key={user.id}
                        onClick={() => {
                          setSelectedUserId(user.id);
                          setShowMessageModal(true);
                        }}
                        className="flex items-center space-x-3 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 active:bg-gray-50 dark:active:bg-gray-700 transition-colors cursor-pointer"
                      >
                        <div className="relative flex-shrink-0">
                          <img
                            className="w-10 h-10 rounded-full object-cover"
                            src={user.avatar || 'https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop'}
                            alt={user.name}
                          />
                          <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white dark:border-gray-800 ${
                            user.is_online ? 'bg-green-500' : 'bg-gray-400'
                          }`} />
                        </div>

                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium truncate ${
                            user.is_online ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'
                          }`}>
                            {user.name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 capitalize truncate">
                            {user.role}
                          </p>
                        </div>

                        <div className="flex items-center gap-2 flex-shrink-0">
                          {unreadCount > 0 && (
                            <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full">
                              {unreadCount}
                            </span>
                          )}
                          <MessageCircle className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Message Modal */}
      <MessageModal
        isOpen={showMessageModal}
        onClose={() => setShowMessageModal(false)}
        userId={selectedUserId}
        onStartVideoCall={onStartVideoCall}
      />

      {/* Group Chat Modal */}
      <GroupChatModal
        isOpen={showGroupChat}
        onClose={() => setShowGroupChat(false)}
        onStartVideoCall={onStartVideoCall}
      />
    </div>
  );
};

export default MobileDashboard;
